<template>
	<fragment>
		<div
			v-if="isLoadingContractData && !contract?.contract_id"
			class="app contract-container"
		>
			<LoadingSpinner />
		</div>
		<div
			v-else-if="showNotFound"
			class="contract-container"
		>
			<NotFound :error="errorMessages[0]" />
		</div>
		<div
			v-else-if="showSigned"
			class="contract-container"
		>
			<Signed />
		</div>
		<div
			v-else-if="contract?.contract_id"
			class="contract-container"
		>
			<div class="page">
				<StatusBanner
					ref="statusBanner"
					:is-staff="isStaff"
					:token="token"
				/>
				<Header :contract="contract" />
				<ConsignorInfo :contract="contract" />
				<Content :contract="contract" />
				<Footer :contract="contract" />
			</div>
		</div>

		<ContractConflictModal
			v-if="showConflictModal"
			:conflicts="conflicts"
			@cancel="handleConflictCancel"
			@resolve="handleConflictResolution"
		/>
	</fragment>
</template>

<script>
	import { Fragment } from 'vue-frag'
	import LoadingSpinner from './components/LoadingSpinner.vue'
	import ConsignorInfo from './sections/ConsignorInfo.vue'
	import Header from './layout/Header.vue'
	import Content from './layout/Content.vue'
	import Footer from './layout/Footer.vue'
	import NotFound from './pages/NotFound.vue'
	import Signed from './pages/Signed.vue'
	import StatusBanner from './components/StatusBanner.vue'
	import ContractConflictModal from './components/ContractConflictModal.vue'
	import contractState from '../../mixins/contractState'
	import { mapActions, mapGetters } from 'vuex'
	import { EventBus, Events } from '../../eventBus'

	export default {
		name: 'ContractForm',
		components: {
			Fragment,
			ConsignorInfo,
			Header,
			Content,
			Footer,
			NotFound,
			Signed,
			LoadingSpinner,
			StatusBanner,
			ContractConflictModal
		},
		mixins: [contractState],
		props: ['token'],
		data() {
			return {
				isOffline: false,
				showConflictModal: false,
				conflicts: [],
				pendingContractData: null,
				isInitialLoad: true
			}
		},
		computed: {
			...mapGetters(['contractData', 'isLoadingContractData']),
			contract() {
				return this.contractData
			},
			/**
			 * Whether all inventory lists and contract are signed by staff
			 * @returns {boolean} True if everything is signed by staff
			 */
			fullySignedByStaff() {
				if (!this.signedByStaff) return false
				if (!this.contract.inventory_lists?.length) return this.signedByStaff
				return this.contract.inventory_lists.every((list) => {
					// Skip lists with null id and 0 items
					if (
						!list.id &&
						(!list.items?.length ||
							list.items.length === 0 ||
							list.items.every((item) => !item.description))
					) {
						return true
					}
					return list.id && list.signed_by_name && list.signed_date
				})
			},
			/**
			 * If the contract is not found or note ready to be signed, show the not found component
			 */
			showNotFound() {
				// Don't show NotFound while still loading
				if (this.isLoadingContractData) {
					return false
				}
				if (!this.contract?.contract_id) {
					return true
				}
				if (this.isStaff === false) {
					if (this.fullySignedByStaff === false) {
						return true
					}
				}
				return false
			},
			/**
			 * If the contract is signed and there are no unsigned inventory lists associated with this contract
			 */
			showSigned() {
				if (this.isStaff) {
					// Staff can always view and sometimes edit after signature
					return false
				}
				if (
					this.signedByStaff === true &&
					this.signedByCustomer === true &&
					this.hasUnsignedInventoryList === false
				) {
					return true
				}
				return false
			}
		},
		watch: {
			// Watch for contract data changes from store and emit to parent
			contractData: {
				handler(newVal) {
					this.$emit('update:contract', newVal)

					// Update document title with contract ID when available
					if (newVal?.contract_id) {
						document.title = `Consignment Contract #${newVal.contract_id}`
					} else {
						document.title = 'Consignment Contract'
					}
				},
				deep: true
			}
		},
		mounted() {
			// Set initial offline status
			this.isOffline = !navigator.onLine

			// Add event listeners
			window.addEventListener('online', this.handleOnlineStatus)
			window.addEventListener('offline', this.handleOnlineStatus)
			window.addEventListener('keydown', this.handleKeyDown)

			// Listen for events
			EventBus.$on(Events.SYNC_OFFLINE_CHANGES, this.handleSyncOfflineChanges)
			EventBus.$on(Events.SHOW_STATUS_BANNER, this.handleStatusBanner)

			// Initial contract load
			this.$nextTick(async () => {
				await this.loadContract()
				this.isInitialLoad = false // Mark initial load as complete
			})
		},
		beforeDestroy() {
			window.removeEventListener('online', this.handleOnlineStatus)
			window.removeEventListener('offline', this.handleOnlineStatus)
			window.removeEventListener('keydown', this.handleKeyDown)
			EventBus.$off(Events.SHOW_STATUS_BANNER, this.handleStatusBanner)
			EventBus.$off(Events.SYNC_OFFLINE_CHANGES, this.handleSyncOfflineChanges)
		},
		methods: {
			...mapActions(['getContract', 'updateContract', 'syncOfflineChanges']),

			/**
			 * Handle keydown events
			 * @param {Event} event - The keydown event
			 */
			handleKeyDown(event) {
				// Check for Ctrl+S or Cmd+S
				if ((event.ctrlKey || event.metaKey) && event.key === 's') {
					event.preventDefault() // Prevent browser's save dialog
					if (this.contract?.contract_id && this.isEditable(true)) {
						this.updateContractData(this.contract)
					}
				}
			},

			/**
			 * Load the contract
			 */
			async loadContract() {
				if (!this.token) return

				try {
					if (this.$refs.statusBanner) {
						this.$refs.statusBanner.showLoading('Loading contract...')
					}

					await this.getContract({
						token: this.token,
						isInitialLoad: this.isInitialLoad
					})

					if (this.contract?.contract_id) {
						if (this.$refs.statusBanner) {
							this.$refs.statusBanner.showSuccess('Contract loaded successfully')
						}
					}
				} catch (error) {
					console.error('Contract loading error:', error)
					if (this.$refs.statusBanner) {
						this.$refs.statusBanner.showError(
							error.response?.data?.message ||
								error.message ||
								'Failed to load contract. Please try again later.'
						)
					}
				}
			},

			/**
			 * Handle online status
			 */
			handleOnlineStatus() {
				const wasOffline = this.isOffline
				this.isOffline = !navigator.onLine

				// Only trigger a reload if:
				// 1. We're coming back online (wasOffline is true and isOffline is false)
				// 2. This isn't the initial load
				// 3. We have a contract loaded
				if (wasOffline && !this.isOffline && !this.isInitialLoad && this.contract?.contract_id) {
					// If we have pending changes, try to update them first
					if (this.pendingContractData) {
						this.updateContractData(this.pendingContractData)
					} else {
						this.loadContract()
					}
				}

				// Update offline status in status banner
				if (this.isOffline) {
					EventBus.$emit(Events.CONTRACT_UNSAVED_CHANGES, this.pendingContractData !== null)
				}
			},
			/**
			 * Handle status banner events
			 * @param {Object} payload - The payload
			 */
			handleStatusBanner({ type, message }) {
				if (!this.$refs.statusBanner) return

				switch (type) {
					case 'loading':
						this.$refs.statusBanner.showLoading(message)
						break
					case 'success':
						this.$refs.statusBanner.showSuccess(message)
						break
					case 'error':
						this.$refs.statusBanner.showError(message)
						break
				}
			},
			/**
			 * Handle conflict cancel
			 */
			handleConflictCancel() {
				this.showConflictModal = false
				this.conflicts = []
				this.pendingContractData = null
			},
			/**
			 * Handle conflict resolution
			 * @param {Object} payload - The payload
			 */
			async handleConflictResolution({ resolution, conflicts }) {
				if (!this.pendingContractData) return

				try {
					if (this.$refs.statusBanner) {
						this.$refs.statusBanner.showLoading('Applying changes...')
					}

					let resolvedData
					switch (resolution) {
						case 'keep-mine':
							// Force update with our changes
							resolvedData = this.pendingContractData
							break

						case 'take-server':
							// Start with current contract data
							resolvedData = { ...this.contract }

							// Get all expense IDs from conflicts to track what we've updated
							const expenseConflictIds = conflicts
								.filter((c) => c.field.startsWith('expenses['))
								.map((c) => c.field.match(/\[(.*?)\]/)?.[1])
								.filter(Boolean)

							// Apply server values for conflicting fields
							for (const conflict of conflicts) {
								// Handle inventory list items specially
								if (conflict.field.startsWith('inventory_list[')) {
									if (!resolvedData.inventory_list) {
										resolvedData.inventory_list = {
											items: []
										}
									}

									// Extract item index/id from field name
									const itemId = conflict.field.match(/\[(.*?)\]/)?.[1]
									if (itemId) {
										// Find existing item or add new one
										const existingItemIndex = resolvedData.inventory_list.items.findIndex(
											(item) => (item.id || '').toString() === itemId
										)

										if (existingItemIndex >= 0) {
											// Update existing item
											resolvedData.inventory_list.items[existingItemIndex] = conflict.serverValue
										} else {
											// Add new item
											resolvedData.inventory_list.items.push(conflict.serverValue)
										}
									}
								}
								// Handle expenses specially
								else if (conflict.field.startsWith('expenses[')) {
									if (!resolvedData.expenses) {
										resolvedData.expenses = []
									}

									// Extract expense ID from field name
									const expenseId = conflict.field.match(/\[(.*?)\]/)?.[1]
									if (expenseId) {
										// Find existing expense or add new one
										const existingExpenseIndex = resolvedData.expenses.findIndex(
											(expense) => expense.id.toString() === expenseId
										)

										if (existingExpenseIndex >= 0) {
											// Update existing expense
											resolvedData.expenses[existingExpenseIndex] = conflict.serverValue
										} else {
											// Add new expense
											resolvedData.expenses.push(conflict.serverValue)
										}
									}
								} else {
									// Handle regular fields
									resolvedData[conflict.field] = conflict.serverValue
								}
							}

							// For expenses, we need to preserve any expenses that weren't in conflicts
							if (this.contract.expenses?.length) {
								const preservedExpenses = this.contract.expenses.filter(
									(expense) => !expenseConflictIds.includes(expense.id.toString())
								)

								if (!resolvedData.expenses) {
									resolvedData.expenses = []
								}

								// Add preserved expenses that don't exist in resolved data
								preservedExpenses.forEach((expense) => {
									const exists = resolvedData.expenses.some(
										(e) => e.id.toString() === expense.id.toString()
									)
									if (!exists) {
										resolvedData.expenses.push(expense)
									}
								})
							}
							break

						default:
							throw new Error('Invalid resolution type')
					}

					// Send the resolved data
					await this.updateContract({
						token: this.token,
						contractData: resolvedData,
						serverContract: this.conflicts[0].serverContract,
						forceUpdate: true
					})

					// Clear conflict state
					this.showConflictModal = false
					this.conflicts = []
					this.pendingContractData = null

					if (this.$refs.statusBanner) {
						this.$refs.statusBanner.showSuccess('Changes applied successfully')
					}
				} catch (error) {
					console.error('Error resolving conflict:', error)
					if (this.$refs.statusBanner) {
						this.$refs.statusBanner.showError(
							error.response?.data?.message ||
								error.message ||
								'Failed to resolve conflict. Please try again.'
						)
					}
				}
			},
			/**
			 * Handle sync offline changes
			 */
			async handleSyncOfflineChanges() {
				if (!this.token) return

				try {
					await this.syncOfflineChanges({ token: this.token })
					if (this.$refs.statusBanner) {
						this.$refs.statusBanner.showSuccess('Changes synced successfully')
					}
				} catch (error) {
					console.error('Error syncing offline changes:', error)
					if (this.$refs.statusBanner) {
						this.$refs.statusBanner.showError(
							'Failed to sync changes. Will try again when connection improves.'
						)
					}
				}
			}
		}
	}
</script>

<style></style>
